import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';

interface NotificationBannerProps {
  onEnableNotifications: () => void;
}

export default function NotificationBanner({ onEnableNotifications }: NotificationBannerProps) {
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const [notificationsSupported, setNotificationsSupported] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [paymentDueSoon, setPaymentDueSoon] = useState(false);

  useEffect(() => {
    if (!user) return;

    // Verificar si el navegador soporta notificaciones
    const checkNotificationSupport = async () => {
      const supported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
      setNotificationsSupported(supported);
      
      if (supported) {
        // Verificar si ya hay una suscripción
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setNotificationsEnabled(!!subscription);
        
        // Mostrar el banner solo si las notificaciones no están habilitadas
        if (!subscription) {
          setShowBanner(true);
        }
      }
    };

    // Verificar si el pago está próximo a vencer
    const checkPaymentStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('business_profiles')
          .select('next_payment_date')
          .eq('id', user.id)
          .single();
        
        if (error) throw error;
        
        if (data?.next_payment_date) {
          const nextPayment = new Date(data.next_payment_date);
          const now = new Date();
          const daysUntilPayment = Math.ceil((nextPayment.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          
          // Si faltan 7 días o menos para el próximo pago
          if (daysUntilPayment <= 7 && daysUntilPayment > 0) {
            setPaymentDueSoon(true);
            setShowBanner(true);
          }
        }
      } catch (error) {
        console.error('Error al verificar estado de pago:', error);
      }
    };

    checkNotificationSupport();
    checkPaymentStatus();
  }, [user]);

  const handleClose = () => {
    setShowBanner(false);
    // Guardar preferencia del usuario para no mostrar el banner de nuevo
    localStorage.setItem('notification_banner_dismissed', 'true');
  };

  if (!showBanner || !user) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        className="bg-gradient-to-r from-blue-50 to-blue-100 border-l-4 border-blue-500 p-4 mb-6 rounded-md shadow-sm"
      >
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Bell className="h-5 w-5 text-blue-500" />
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm text-blue-700">
              {paymentDueSoon ? (
                <>
                  <span className="font-medium">¡Tu próximo pago está cerca!</span> Activa las notificaciones para recibir recordatorios y no perder tu suscripción.
                </>
              ) : (
                <>
                  <span className="font-medium">¡Mantente informado!</span> Activa las notificaciones para recibir recordatorios de pago y otras alertas importantes.
                </>
              )}
            </p>
            <div className="mt-2">
              {notificationsSupported && !notificationsEnabled && (
                <button
                  onClick={onEnableNotifications}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-xs px-3 py-1.5 rounded-md mr-2 shadow-sm"
                >
                  Activar notificaciones
                </button>
              )}
              <button
                onClick={handleClose}
                className="text-blue-700 hover:text-blue-900 text-xs"
              >
                Cerrar
              </button>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="flex-shrink-0 ml-2"
          >
            <X className="h-5 w-5 text-blue-500" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}